/**
 * The battle log (PRD S5): every line of the sim's play log, each shown when the event it
 * describes plays, in plain English with the numbers in a small sub-line under it; a header per
 * snap and a line per game event between snaps. It lives in the sheet (slice 8).
 */
import type { LogEntry } from '@tdmons/sim';
import { SUPER_EFFECTIVE } from '../screen/reader';

export class LogPanel {
  private readonly list: HTMLOListElement;
  private entries: readonly LogEntry[] = [];
  private shown = 0;

  constructor(private readonly root: HTMLElement) {
    const list = root.querySelector('ol');
    if (!list) throw new Error('the log panel needs an <ol>');
    this.list = list;
  }

  /** Empty the panel (a new seed, a replay). */
  reset(): void {
    this.list.replaceChildren();
    this.entries = [];
    this.shown = 0;
  }

  /** Start a snap's lines; with a header when several snaps share the panel. */
  beginSnap(entries: readonly LogEntry[], header?: string): void {
    this.entries = entries;
    this.shown = 0;
    if (header !== undefined) {
      const li = document.createElement('li');
      li.className = 'log-snap';
      li.textContent = header;
      this.list.append(li);
      this.root.scrollTop = this.root.scrollHeight;
    }
  }

  /** The event at this index has played: show its line, and any earlier ones not shown yet. */
  reveal(eventIndex: number): void {
    while (this.shown < this.entries.length) {
      const entry = this.entries[this.shown] as LogEntry;
      if (entry.event > eventIndex) break;
      const li = document.createElement('li');
      li.className = entry.line.includes(SUPER_EFFECTIVE) ? 'log-line log-super' : 'log-line';
      li.dataset.line = entry.line;
      li.textContent = entry.text;
      if (entry.numbers) {
        const sub = document.createElement('span');
        sub.className = 'log-numbers';
        sub.textContent = entry.numbers;
        li.append(sub);
      }
      this.list.append(li);
      this.shown++;
    }
    this.root.scrollTop = this.root.scrollHeight;
  }

  /** The sim's lines on screen, in order (what the tests read). */
  lines(): string[] {
    return [...this.list.querySelectorAll<HTMLElement>('.log-line')].map(
      (li) => li.dataset.line ?? '',
    );
  }
}
